// The connection: handshake, dispatch, continuity, heartbeat, reconnect.
//
// One WebSocket carries eleven message classes, and this is the demultiplexer for
// all of them. The rules it enforces, and where each comes from:
//
//   - HELLO is the first message on the socket and carries the raw token exactly
//     once (SPEC §4). Everything after it names the SESSION.
//
//   - Continuity is judged on TICK alone (§3). A chunk absent from a tick's dirty
//     bitmap was QUIET, not lost; a chunk present in it whose DELTA never arrives
//     is a real gap. That judgement lives in Follower, ported from
//     internal/wire/follow.go; this file only feeds it.
//
//   - On a gap: discard the board, ignore deltas, wait for the next BOARD-SCOPE
//     keyframe. A chunk keyframe restores one chunk and would leave a client
//     subscribed to nine of them resuming with eight stale, so it does not end a
//     gap here.
//
//   - A keyframe REPLACES its tick's TICK and DELTAs rather than accompanying
//     them (§3). The server sends no TICK and no DELTA for a tick it gave this
//     client a keyframe for, and Follower.observeKeyframe resyncs the tick line
//     to it rather than reading the absence as loss.
//
//   - An empty SUBSCRIBE is the heartbeat. The server holds a 120 s read deadline
//     and closes with RejectIdle otherwise; the message carries the last applied
//     tick in its header, so it doubles as a staleness report and costs 13 bytes.
//
// FRAME ORDER IS LOAD-BEARING. Decoding a KEYFRAME is asynchronous, because
// gunzip in the browser is `DecompressionStream`. Every inbound message therefore
// goes through one serial queue: a TICK processed while its keyframe was still
// inflating would be judged against a tick line the keyframe was about to move,
// and read as a gap on a board that was about to be replaced.

import {
  ClassAggregate,
  ClassDelta,
  ClassEphemeral,
  ClassKeyframe,
  ClassReject,
  ClassSelf,
  ClassTick,
  ClassWelcome,
  parseHeader,
  type Header,
} from '../wire/classes';
import { ChunkSet } from '../wire/chunkset';
import { NumChunks, type ChunkID, type ClickKind, type PixelID, type TickNo } from '../wire/core';
import { WireError } from '../wire/errors';
import { decodeAggregates, type Aggregates } from '../wire/aggregate';
import { decodeDelta } from '../wire/delta';
import { decodeEphemeral, type Ephemeral } from '../wire/ephemeral';
import { ScopeBoard, decodeKeyframe, type Keyframe } from '../wire/keyframe';
import { decodeSelf, type Self } from '../wire/self';
import { decodeTick } from '../wire/tick';
import { encodeSubscribe } from '../wire/subscribe';
import {
  decodeReject,
  decodeWelcome,
  encodeClick,
  encodeHello,
  rejectReconnect,
  type Reject,
  type SessionID,
  type Welcome,
} from '../wire/clientmsg';
import { BoardReplica } from './board';
import { ClickBook, type OptimisticClick } from './clicks';
import { Follower, StatusGap, StatusOK } from './follower';
import { webSocketTransport, type Transport, type TransportFactory } from './transport';

export type ConnectionState = 'idle' | 'connecting' | 'handshaking' | 'live' | 'closed';

export interface ConnectionEvents {
  /** The handshake answer: the session id and the country the token is locked to. */
  onWelcome?(w: Welcome): void;
  /** A tick has been fully applied — every DELTA it announced has arrived. The render signal. */
  onFrame?(tick: TickNo): void;
  /** A whole board (or one chunk) has been replaced. */
  onKeyframe?(kf: Keyframe): void;
  /** The bank moved or a click was judged. `resolved` is the click the verdict was about, if any. */
  onSelf?(s: Self, resolved: OptimisticClick | null): void;
  onAggregates?(a: Aggregates): void;
  onEphemeral?(e: Ephemeral): void;
  /** Frames were missed. The board has been discarded and the client is waiting for a keyframe. */
  onGap?(atTick: TickNo): void;
  /** The server is closing and said why, in a form worth rendering. */
  onReject?(r: Reject): void;
  onStateChange?(s: ConnectionState): void;
  /** A decode failure, a transport error, or anything else worth a log line. */
  onError?(e: unknown): void;
}

export interface ConnectionOptions {
  /** The socket URL. Relative in the browser; the dev server proxies /ws. */
  url: string;
  /** Fetches the identity token — SPEC §4's /session endpoint, or anything that returns one. */
  token: () => Promise<string> | string;
  events?: ConnectionEvents;
  transport?: TransportFactory;
  /** Injected for tests. */
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (h: unknown) => void;
  /**
   * How often the empty SUBSCRIBE heartbeat goes out. The server's read deadline
   * is 120 s; the default here is a quarter of that, so two lost beats are still
   * not a disconnect.
   */
  heartbeatMs?: number;
  /** Backoff bounds for a reconnect the server asked for. */
  reconnectMinMs?: number;
  reconnectMaxMs?: number;
  clickBook?: ClickBook;
  board?: BoardReplica;
}

export class Connection {
  readonly board: BoardReplica;
  readonly follower = new Follower();
  readonly clicks: ClickBook;

  private readonly opts: Required<
    Pick<ConnectionOptions, 'heartbeatMs' | 'reconnectMinMs' | 'reconnectMaxMs'>
  > &
    ConnectionOptions;
  private readonly ev: ConnectionEvents;
  private readonly factory: TransportFactory;
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (h: unknown) => void;

  private sock: Transport | null = null;
  private state: ConnectionState = 'idle';
  private session: SessionID | null = null;
  private welcome: Welcome | null = null;

  /** The chunks this client wants deltas for. Kept in step with what is on the wire. */
  private subs = new ChunkSet();

  private queue: Uint8Array[] = [];
  private pumping = false;

  private heartbeat: unknown = null;
  private reconnectTimer: unknown = null;
  private reconnectDelay: number;
  private wantReconnect = false;
  private generation = 0;

  constructor(opts: ConnectionOptions) {
    this.opts = {
      heartbeatMs: 30_000,
      reconnectMinMs: 500,
      reconnectMaxMs: 15_000,
      ...opts,
    };
    this.ev = opts.events ?? {};
    this.factory = opts.transport ?? webSocketTransport;
    this.now = opts.now ?? (() => Date.now());
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.reconnectDelay = this.opts.reconnectMinMs;
    this.board = opts.board ?? new BoardReplica();
    this.clicks = opts.clickBook ?? new ClickBook({ now: this.now });
  }

  currentState(): ConnectionState {
    return this.state;
  }

  /** The country the token is locked to, or 0 while geo-IP has not placed the player. */
  country(): number {
    return this.welcome?.country ?? 0;
  }

  countryLocked(): boolean {
    return this.welcome?.locked ?? false;
  }

  /** The chunks currently subscribed. */
  subscriptions(): ChunkSet {
    return this.subs;
  }

  async connect(): Promise<void> {
    if (this.state === 'connecting' || this.state === 'handshaking' || this.state === 'live') return;
    this.wantReconnect = true;
    const gen = ++this.generation;
    this.setState('connecting');
    let token: string;
    try {
      token = await this.opts.token();
    } catch (e) {
      this.fail(e);
      this.scheduleReconnect();
      return;
    }
    if (gen !== this.generation) return; // superseded by a close or another connect

    this.sock = this.factory(this.opts.url, {
      onOpen: () => {
        if (gen !== this.generation) return;
        try {
          this.sock?.send(encodeHello(token));
          this.setState('handshaking');
        } catch (e) {
          this.fail(e);
        }
      },
      onMessage: (b) => {
        if (gen !== this.generation) return;
        this.enqueue(b);
      },
      onClose: () => {
        if (gen !== this.generation) return;
        this.teardown();
        this.setState('closed');
        if (this.wantReconnect) this.scheduleReconnect();
      },
      onError: (e) => {
        if (gen !== this.generation) return;
        this.fail(e);
      },
    });
  }

  /** Closes and stops reconnecting. */
  close(code = 1000, reason = ''): void {
    this.wantReconnect = false;
    this.generation++;
    this.teardown();
    this.sock?.close(code, reason);
    this.sock = null;
    this.setState('closed');
  }

  // --- outbound -----------------------------------------------------------

  /**
   * Replaces the viewport subscription and puts the pan diff on the wire.
   *
   * A diff, not a set: a pan of one chunk otherwise resends the whole viewport
   * every frame of a drag. The follower is moved in the same call so that the set
   * completeness is judged against never drifts from the set the server has.
   */
  setSubscriptions(next: ChunkSet): void {
    const add: ChunkID[] = [];
    const remove: ChunkID[] = [];
    for (let c = 0; c < NumChunks; c++) {
      const want = next.has(c);
      const have = this.subs.has(c);
      if (want && !have) add.push(c);
      else if (!want && have) remove.push(c);
    }
    this.subs = next.clone();
    this.follower.setSubscriptions(this.subs);
    if (add.length === 0 && remove.length === 0) return;
    this.send(encodeSubscribe({ tick: this.follower.lastTick(), add, remove }));
  }

  /**
   * Sends one click and returns the optimistic record for it, which the renderer
   * draws on the next frame — before the server has said anything.
   *
   * Returns null when the socket is not live: a click that was never sent must
   * not leave a marker on the map claiming it was.
   */
  click(target: PixelID, kind: ClickKind): OptimisticClick | null {
    if (this.state !== 'live' || !this.session) return null;
    const c = this.clicks.intend(target, kind, this.country());
    try {
      this.send(
        encodeClick({
          tick: this.follower.lastTick(),
          session: this.session,
          target,
          kind,
          seq: c.seq,
        }),
      );
    } catch (e) {
      this.fail(e);
      return null;
    }
    return c;
  }

  /**
   * The heartbeat: an empty SUBSCRIBE carrying the last applied tick.
   *
   * The server's read deadline closes an idle connection with RejectIdle, and its
   * own reject text names this message as the fix. It is also the only staleness
   * signal the server gets, and it costs it nothing to read.
   */
  sendHeartbeat(): void {
    if (this.state !== 'live') return;
    this.send(encodeSubscribe({ tick: this.follower.lastTick() }));
  }

  private send(b: Uint8Array): void {
    this.sock?.send(b);
  }

  // --- inbound ------------------------------------------------------------

  private enqueue(b: Uint8Array): void {
    this.queue.push(b);
    if (!this.pumping) void this.pump();
  }

  /**
   * The serial pump. One message at a time, in arrival order, with the await for
   * a keyframe's gunzip inside the loop rather than around it.
   */
  private async pump(): Promise<void> {
    this.pumping = true;
    try {
      while (this.queue.length > 0) {
        const b = this.queue.shift()!;
        try {
          await this.handle(b);
        } catch (e) {
          this.fail(e);
          if (e instanceof WireError) {
            // A frame this client cannot parse means the two implementations have
            // parted company. Reconnecting would replay the same bytes, so stop.
            this.close(1002, 'protocol');
            return;
          }
        }
      }
    } finally {
      this.pumping = false;
    }
  }

  private async handle(b: Uint8Array): Promise<void> {
    const h: Header = parseHeader(b);

    if (this.state === 'handshaking') {
      if (h.class === ClassWelcome) {
        const w = decodeWelcome(b);
        this.session = w.session;
        this.welcome = w;
        this.setState('live');
        this.reconnectDelay = this.opts.reconnectMinMs;
        this.follower.reset();
        this.board.invalidate();
        this.clicks.reset();
        // Re-declare the viewport: a fresh connection subscribes to nothing until
        // it says otherwise, because a server that guessed a viewport would be
        // inventing the per-client state §3 exists to avoid.
        const want = this.subs.clone();
        this.subs = new ChunkSet();
        this.setSubscriptions(want);
        this.startHeartbeat();
        this.ev.onWelcome?.(w);
        return;
      }
      if (h.class === ClassReject) {
        this.handleReject(decodeReject(b));
        return;
      }
      throw new Error(`net: expected WELCOME, got class ${h.class}`);
    }

    switch (h.class) {
      case ClassKeyframe:
        await this.handleKeyframe(b);
        return;
      case ClassTick: {
        const t = decodeTick(b);
        const st = this.follower.observeTick(t);
        if (st === StatusGap) {
          this.gap(t.tick);
          return;
        }
        if (st !== StatusOK) return;
        // A tick with nothing outstanding is a complete frame the instant it
        // arrives, which is the calm-viewport case: the render signal fires
        // without waiting for a DELTA that was never coming.
        if (this.follower.tickComplete()) this.ev.onFrame?.(t.tick);
        return;
      }
      case ClassDelta: {
        const d = decodeDelta(b);
        const st = this.follower.observeDelta(d.tick, d.chunk);
        if (st === StatusGap) {
          this.gap(d.tick);
          return;
        }
        if (st !== StatusOK) return;
        this.board.applyDelta(d);
        if (this.follower.tickComplete()) this.ev.onFrame?.(d.tick);
        return;
      }
      case ClassSelf: {
        const s = decodeSelf(b);
        const resolved = this.clicks.applySelf(s);
        this.ev.onSelf?.(s, resolved);
        return;
      }
      case ClassAggregate:
        this.ev.onAggregates?.(decodeAggregates(b));
        return;
      case ClassEphemeral:
        this.ev.onEphemeral?.(decodeEphemeral(b));
        return;
      case ClassReject:
        this.handleReject(decodeReject(b));
        return;
      default:
        // WELCOME after the handshake, or a client-to-server class coming back at
        // us. Neither is state; neither may disturb the tick line.
        return;
    }
  }

  private async handleKeyframe(b: Uint8Array): Promise<void> {
    const kf = await decodeKeyframe(b);
    if (kf.scope === ScopeBoard) {
      const st = this.follower.observeKeyframe(kf.tick);
      if (st !== StatusOK) return; // an old keyframe; the board already has better
      this.board.applyKeyframe(kf);
      this.ev.onKeyframe?.(kf);
      this.ev.onFrame?.(kf.tick);
      return;
    }
    // Chunk scope. §3: a gap is ended by a BOARD-scope keyframe, because a chunk
    // keyframe restores only its own chunk and a client subscribed to nine of them
    // would resume with eight stale. So while gapped, this is not the frame we are
    // waiting for and it is not applied to a board that is being thrown away.
    if (this.follower.needsKeyframe()) return;
    const st = this.follower.observeKeyframe(kf.tick);
    if (st !== StatusOK) return;
    this.board.applyKeyframe(kf);
    this.ev.onKeyframe?.(kf);
  }

  private gap(atTick: TickNo): void {
    this.board.invalidate();
    this.ev.onGap?.(atTick);
  }

  private handleReject(r: Reject): void {
    this.ev.onReject?.(r);
    this.wantReconnect = rejectReconnect(r.code);
    // The socket close follows from the server. Nothing to do but stop sending.
    this.stopHeartbeat();
  }

  // --- plumbing -----------------------------------------------------------

  private setState(s: ConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    this.ev.onStateChange?.(s);
  }

  private fail(e: unknown): void {
    this.ev.onError?.(e);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    if (this.opts.heartbeatMs <= 0) return;
    const beat = () => {
      this.sendHeartbeat();
      this.heartbeat = this.setTimer(beat, this.opts.heartbeatMs);
    };
    this.heartbeat = this.setTimer(beat, this.opts.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      this.clearTimer(this.heartbeat);
      this.heartbeat = null;
    }
  }

  private teardown(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      this.clearTimer(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.queue.length = 0;
    this.session = null;
  }

  /**
   * Backoff with jitter. Rung 4 of the §3 ladder is an explicit reconnect hint —
   * a fresh connection starts at rung 0 and is handed a keyframe — so coming back
   * is correct, and coming back in lockstep with every other shed client is how a
   * server that shed one connection sheds all of them.
   */
  private scheduleReconnect(): void {
    if (!this.wantReconnect || this.reconnectTimer !== null) return;
    const jitter = 0.5 + Math.random() * 0.5;
    const wait = Math.min(this.reconnectDelay, this.opts.reconnectMaxMs) * jitter;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.opts.reconnectMaxMs);
    this.reconnectTimer = this.setTimer(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, wait);
  }
}
